/**
 * atomberg-support-address.js
 *
 * Pincode → city/state auto-fill for Support page forms. Binds to every
 * [data-support-address] block (pincode input + city/state inputs) and calls
 * GET {api_base_url}/api/support/pincode-lookup?pincode= on a valid 6-digit
 * entry, filling city/state and (only on blocks with
 * data-support-address-installation) showing an "installation not available
 * here" message when the response says so.
 *
 * City/state auto-fill is a convenience — an API error just leaves them
 * for manual entry rather than blocking the form. Installation is the one
 * exception and FAILS CLOSED, matching the reference site: Continue stays
 * disabled until this pincode is positively confirmed serviceable. The
 * state machine is stamped on the form root as
 * data-support-installation-serviceable = "checking" | "true" | "false"
 * (absent = no valid pincode looked up yet); a lookup failure counts as
 * not serviceable. atomberg-support-forms.js re-checks it on
 * Continue/submit as a backstop, and a loader row shows while checking.
 */
(function () {
  'use strict';

  /** @returns {string} */
  function getApiBaseUrl() {
    var root = document.querySelector('[data-support-api-base-url]');
    return (root && root.getAttribute('data-support-api-base-url')) || '';
  }

  /**
   * @param {string} apiBaseUrl
   * @param {string} pincode
   * @param {string} product - selected product type value; Salesforce's
   *   zipcodeapi is product-scoped, matching the reference's per-type check
   * @returns {Promise<{status: boolean, city: string, state: string, installation_serviceable: boolean, message: string}>}
   */
  function lookupPincode(apiBaseUrl, pincode, product) {
    var url = apiBaseUrl + '/api/support/pincode-lookup?pincode=' + encodeURIComponent(pincode);
    if (product) url += '&product=' + encodeURIComponent(product);
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .catch(function () {
        return { status: false, city: '', state: '', installation_serviceable: true, message: '' };
      });
  }

  /** @param {HTMLElement} block */
  function init(block) {
    if (/** @type {any} */ (block)._supportAddressBound) return;
    /** @type {any} */ (block)._supportAddressBound = true;

    var pincodeInput = /** @type {HTMLInputElement|null} */ (
      block.querySelector('[data-support-address-pincode]')
    );
    var formRoot = /** @type {HTMLElement|null} */ (block.closest('[data-support-form-root]'));
    // City/state may live in a SEPARATE address block (installation renders
    // pincode_only up top and skip_pincode further down) — fall back to the
    // form root so the lookup can still auto-fill them.
    var fieldScope = formRoot || block;
    var cityInput = /** @type {HTMLInputElement|null} */ (
      block.querySelector('[data-support-address-city]') ||
        fieldScope.querySelector('[data-support-address-city]')
    );
    var stateInput = /** @type {HTMLInputElement|null} */ (
      block.querySelector('[data-support-address-state]') ||
        fieldScope.querySelector('[data-support-address-state]')
    );
    var errorEl = /** @type {HTMLElement|null} */ (
      block.querySelector('[data-support-address-error]')
    );
    var isInstallation = block.hasAttribute('data-support-address-installation');
    if (!pincodeInput) return;
    var input = /** @type {HTMLInputElement} */ (pincodeInput);
    var checkingEl = /** @type {HTMLElement|null} */ (
      block.querySelector('[data-support-address-checking]')
    );

    /** @param {'checking'|'true'|'false'|null} state - null = no verdict yet */
    function setServiceableState(state) {
      if (!isInstallation || !formRoot) return;
      if (state) {
        formRoot.setAttribute('data-support-installation-serviceable', state);
      } else {
        formRoot.removeAttribute('data-support-installation-serviceable');
      }
      if (checkingEl) checkingEl.hidden = state !== 'checking';
      // Mirror the reference: the step's Continue button is disabled until
      // the pincode is confirmed serviceable, not just message-gated.
      var continueBtn = /** @type {HTMLButtonElement|null} */ (
        formRoot.querySelector('[data-support-form-continue]')
      );
      if (continueBtn) continueBtn.disabled = state !== 'true';
    }

    /** @returns {string} the form's selected product type ('' when unset) */
    function currentProduct() {
      if (!formRoot) return '';
      var select = /** @type {HTMLSelectElement|null} */ (
        formRoot.querySelector('[data-support-product-type]')
      );
      return (select && select.value) || '';
    }

    // Last pincode+product combination actually looked up, so typing further
    // digits or re-focusing/blurring without a real change doesn't refire the
    // API call — but changing the product type does (serviceability is
    // product-scoped in Salesforce, like the reference).
    var lastChecked = '';

    function runLookup() {
      // An empty product resolves to the default category and reports unserviceable — a false negative the shopper can't act on.
      if (isInstallation && !currentProduct()) return;
      var pincode = input.value;
      var lookupKey = pincode + '|' + currentProduct();
      if (!/^\d{6}$/.test(pincode) || lookupKey === lastChecked) return;

      var apiBaseUrl = getApiBaseUrl();
      if (!apiBaseUrl) {
        // Silently doing nothing here looks identical to "not implemented
        // yet" from the user's side — surface it instead of guessing.
        if (errorEl) {
          errorEl.textContent = 'Delivery lookup is not configured for this store.';
          errorEl.hidden = false;
        }
        return;
      }

      lastChecked = lookupKey;
      setServiceableState('checking');
      lookupPincode(apiBaseUrl, pincode, currentProduct()).then(function (res) {
        // Guard against a stale response landing after the user edited the
        // pincode or product type again — only the latest lookup may set
        // the verdict.
        if (input.value + '|' + currentProduct() !== lookupKey) return;

        var serviceable = !!res.status && res.installation_serviceable !== false;
        setServiceableState(serviceable ? 'true' : 'false');

        if (!res.status) {
          // Lookup failed or the pincode wasn't recognized. City/state
          // auto-fill is a convenience, so leave them for manual entry —
          // but for installation this still counts as NOT serviceable
          // (fail closed, set above) and the user sees why.
          if (isInstallation && errorEl) {
            errorEl.textContent =
              'Sorry for the inconvenience, we are currently not available at your location. Please check your pincode and try again.';
            errorEl.hidden = false;
          }
          return;
        }
        if (cityInput && res.city) cityInput.value = res.city;
        if (stateInput && res.state) stateInput.value = res.state;
        if (isInstallation && errorEl) {
          if (!serviceable) {
            errorEl.textContent =
              'Sorry for the inconvenience, we are currently not available at your location. Please check your pincode and try again.';
            errorEl.hidden = false;
          } else {
            errorEl.hidden = true;
          }
        } else if (errorEl) {
          errorEl.hidden = true;
        }
        block.dispatchEvent(
          new CustomEvent('atomberg:support-address-resolved', {
            detail: {
              pincode: pincode,
              city: res.city,
              state: res.state,
              installationServiceable: res.installation_serviceable,
            },
            bubbles: true,
          })
        );
      });
    }

    input.addEventListener('input', function () {
      input.value = input.value.replace(/\D/g, '').slice(0, 6);
      if (errorEl) errorEl.hidden = true;
      // Editing the pincode invalidates the previous verdict — back to "no
      // verdict", which keeps installation's Continue disabled (fail closed).
      if (input.value + '|' + currentProduct() !== lastChecked) setServiceableState(null);
      // Fire as soon as the 6th valid digit lands — don't make the user
      // click/tab away from the field for it to feel "automatic".
      if (input.value.length === 6) runLookup();
    });

    // Serviceability is product-scoped — switching product type after
    // entering a pincode must re-check, exactly like the reference.
    if (isInstallation && formRoot) {
      formRoot.addEventListener('change', function (e) {
        var target = /** @type {HTMLElement} */ (e.target);
        if (!target || !target.matches || !target.matches('[data-support-product-type]')) return;
        setServiceableState(null);
        if (errorEl) errorEl.hidden = true;
        if (/^\d{6}$/.test(input.value)) runLookup();
      });
    }

    // Installation starts with no verdict — Continue is disabled from the
    // first paint until a pincode checks out, exactly like the reference.
    setServiceableState(null);

    // Kept as a fallback for the paste-then-tab case (input event still
    // fires on paste in evergreen browsers, but blur is a cheap safety net).
    input.addEventListener('blur', runLookup);
  }

  function initAll() {
    document.querySelectorAll('[data-support-address]').forEach(function (el) {
      init(/** @type {HTMLElement} */ (el));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
